import {
    useContext, useMemo, useState,
} from 'react';
import { DataGrid, GridColumnHeaderSortIcon, useGridApiRef } from '@mui/x-data-grid';
import {
    Box, Checkbox, FormControlLabel, IconButton, Link, MenuItem, TextField,
    Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import _ from 'lodash';

import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { useSelector } from 'BMapsRedux';
import { JobTypes, lookupJobDefinition } from 'BMapsSrc/jobman';
import { CompoundHeritage } from 'BMapsModel';
import { updateLocalJobState } from 'BMapsRedux/jobs/access';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import ExternalLink from 'BMapsUI/common/ExternalLink';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { HoverableCompoundStructureCell } from 'BMapsUI/tables/TableCells';
import { DialogCancelButton, DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { wrapForDisabledTooltip } from 'BMapsUI/common/helpers';
import Select from 'BMapsUI/common/Select';
import { ProteinContext } from 'BMapsUI/common/ProteinContextProvider';
import ProteinSelect from 'BMapsUI/common/ProteinSelect';
import { setJobsCompoundsTableState } from 'BMapsRedux/projectState/access';
import { GridToolBar } from 'BMapsUI/tables/toolbars/CustomToolbars';
import { CustomColumnsPanel } from 'BMapsUI/tables/CustomColumnsPanel';
import { MolDataSource } from 'BMapsSrc/utils';
import {
    getPropertyTooltip, hasPropertyTooltipDetail,
} from 'BMapsSrc/integrations/properties_rendering';
import { applyColumnDirectionality, getFormattedValue } from 'BMapsSrc/ui/tables/BMapsTable';
import { validateCompoundName } from 'BMapsSrc/util/mol_info_utils';
import { makeOneSDF } from 'BMapsUtil/mol_format_utils';
import { downloadData } from 'BMapsUI/ui_utils';
import { getPlacementOptions, PlacementTypes, getMoleculeOptions } from '../../tab_utils';
import { ImportErrors, InputBox } from '../../import_components';

export function JobsResultsTab({ closeImportPane, selected }) {
    const {
        localJobState, displayedCompoundJobs,
    } = useSelector((state) => state.jobs);

    let message; // content when no table to display
    if (!displayedCompoundJobs?.length) {
        message = (
            <span>
                No compound results to display.
                <br />
                Add results from the
                {' '}
                <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                    component="button"
                    onClick={() => UserActions.OpenImport('jobs_compounds_tab')}
                    sx={{ color: 'var(--marketing-blue)' }}
                >
                    Compound Jobs Tab
                </Link>
                {' '}
                to view them here.
            </span>
        );
    }

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden',
        }}
        >
            {!!selected && (
            <Typography
                component="div"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {message || (
                <CompoundJobGrid
                    closeImportPane={closeImportPane}
                    activeJobs={displayedCompoundJobs}
                    localJobState={localJobState}
                />
                )}
            </Typography>
            )}
        </Box>
    );
}

function CompoundJobGrid({
    closeImportPane, activeJobs, localJobState,
}) {
    const apiRef = useGridApiRef();
    const tableState = useSelector((state) => state.projectState.tableState.jobsCompoundsTable);
    const columnWidths = tableState?.columnWidths || {};
    const setColumnWidths = (widths) => {
        setJobsCompoundsTableState({
            columnWidths: widths,
        });
    };

    // Memoised on the two redux values it derives from, not for its own cost -- it exists so
    // `defaultColumnVisibility` below has a stable dependency. Rebuilt inline, this array got a
    // new identity every render, which would make any memo keyed on it miss every time.
    const compoundJobs = useMemo(() => activeJobs.map((job) => (
        {
            job,
            structures: localJobState[`${job.jobid}`]?.structures || [],
        }
    )), [activeJobs, localJobState]);

    // Walks every structure of every displayed job, so it is worth not repeating on each
    // render: a single REINVENT job can return thousands of rows.
    const defaultColumnVisibility = useMemo(
        () => getDefaultColumnVisibilityModel(compoundJobs), [compoundJobs]
    );

    // The kinds of job being displayed, once per job type.
    const displayedJobDefinitions = [...new Set(activeJobs.map((job) => job.jobtype))]
        .map(lookupJobDefinition)
        .filter(Boolean);
    // Name the program when there's only one; a mix of programs gets the generic title.
    const resultsTitle = displayedJobDefinitions.length === 1
        ? `${displayedJobDefinitions[0].getJobTypeName()} Job Results`
        : 'Compound Job Results';
    // The kinds of displayed job that have something to say about what they do and produce.
    const describedJobDefinitions = displayedJobDefinitions
        .filter((jobDefinition) => jobDefinition.getJobDescription());

    const { selectedCaseData } = useContext(ProteinContext);
    const [cmpdName, setCmpdName] = useState('');
    const [checkedRowItems, setCheckedRowItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [continueAdding, setContinueAdding] = useState(false);
    const [loadErrors, setLoadErrors] = useState([]);
    const placementOptions = useMemo(() => {
        let placementFmt = 'smi'; // used to get placement options
        if (checkedRowItems.length > 0) {
            if (checkedRowItems.some(structHas3d)) placementFmt = 'mol';
        } else {
            if (compoundJobs.some((cj) => cj.structures.some(structHas3d))) placementFmt = 'mol';
        }
        const {
            availableOptions, disabledOptions,
        } = getPlacementOptions(placementFmt, selectedCaseData);
        return [...availableOptions, ...disabledOptions];
    }, [selectedCaseData, checkedRowItems]);
    const [placement, setPlacement] = useState(() => {
        const all3D = compoundJobs.length > 0 && compoundJobs.every(
            (cj) => cj.structures.length > 0 && cj.structures.every(structHas3d)
        );
        return all3D ? PlacementTypes.KeepExisting : PlacementTypes.PlaceOutside;
    });
    function findPlacement(value, placementOptionList) {
        return placementOptionList.find((option) => option.value === value);
    }
    function structHas3d(s) {
        return s.mol || s.sdf;
    }

    /**
     * @param {Array<Object>} importCmpdRows The selected compoound row objects to import
     */
    async function importMolecules(importCmpdRows) {
        const allErrors = [];
        const allWarnings = [];

        try {
            const placementOption = findPlacement(placement, placementOptions);
            if (!placementOption) {
                setLoadErrors(['Please choose a compound placement option.']);
                return;
            }

            setLoading(true);
            setLoadErrors([]); // Clear previous errors
            const { placementType, ligandspec: refSpec } = placementOption;
            const moleculeLoadOptions = getMoleculeOptions(placementType, true, refSpec || '', false);
            for (const importCmpdRow of importCmpdRows) {
                // Load each compound one at a time, along with properties and heritage
                let moldata = importCmpdRow.SMILES;
                let format = 'smi';
                if (importCmpdRow.sdf) {
                    moldata = importCmpdRow.sdf;
                    format = 'sdf';
                } else if (importCmpdRow.mol) {
                    moldata = importCmpdRow.mol;
                    format = 'mol';
                }
                const jobDefinition = lookupJobDefinition(importCmpdRow.jobtype);

                let molSource = new MolDataSource({
                    compoundName: cmpdName,
                    molFormat: format,
                    molData: moldata,
                });
                let addHeritage;

                // Special handling for certain jobs: molsource (load time) and heritage (post load)
                if (importCmpdRow.jobtype === JobTypes.REINVENT) {
                    molSource = MolDataSource.FromCompoundGenerationJob(cmpdName, format, moldata);
                    const inputInchi = importCmpdRow.Input_inchikey;
                    const loadedCompounds = App.Workspace.getLoadedCompounds();
                    const originalCompound = loadedCompounds.find((c) => c.getProperty('structure2d', 'inchikey') === inputInchi);
                    if (originalCompound) {
                        molSource.modifiedFrom = originalCompound.resSpec;
                    }
                    addHeritage = (cmpd) => {
                        const sourceCmpd = App.Workspace.getCompoundBySpec(molSource.modifiedFrom);
                        const heritageDetail = { ...importCmpdRow };
                        if (!sourceCmpd) {
                            heritageDetail.sourceParentId = importCmpdRow.Input_SMILES;
                        }
                        CompoundHeritage.addCompoundGeneration(sourceCmpd, cmpd, heritageDetail);
                    };
                }

                if (importCmpdRow.jobtype === JobTypes.DRUGFLOW) {
                    molSource = MolDataSource.FromCompoundGenerationJob(cmpdName, format, moldata);
                    // Using the pocket reference ligand for the heritage parent.
                    // Differs from REINVENT where the heritage parent is the actual input ligand,
                    // but it might help with reproducibility.
                    addHeritage = (cmpd) => {
                        const pocketCmpd = importCmpdRow.pocketLigand
                            && selectedCaseData?.getCompoundBySpec(importCmpdRow.pocketLigand);
                        CompoundHeritage.addCompoundGeneration(
                            pocketCmpd || null, cmpd, { ...importCmpdRow }
                        );
                    };
                }

                if (importCmpdRow.jobtype === JobTypes.GNINA_DOCKING) {
                    molSource.sourceType = MolDataSource.Types.GninaDocking;
                    addHeritage = (cmpd) => {
                        const heritageDetailObj = {
                            dockingProgram: 'gnina',
                            dockingScore: importCmpdRow.affinity,
                            gninaAffinity: importCmpdRow.cnn_affinity,
                            gninaPoseScore: importCmpdRow.cnn_score,
                            gninaVS: importCmpdRow.cnn_vs,
                        };
                        let sourceCmpd;
                        const sourceSpec = cmpd.getProperty('sdfProps', 'tmpSpec');
                        if (sourceSpec) {
                            sourceCmpd = App.Workspace.getCompoundBySpec(sourceSpec);
                        }
                        if (!sourceCmpd) {
                            heritageDetailObj.sourceParentId = importCmpdRow.SMILES;
                        }
                        CompoundHeritage.addDock(sourceCmpd, cmpd, heritageDetailObj);
                        // Clear temp properties added for loading
                        cmpd.scopedProperties.removeProperty('sdfProps', 'tmpI');
                        cmpd.scopedProperties.removeProperty('sdfProps', 'tmpSpec');
                    };
                }

                // Actually add to the workspace
                const { compounds: [compound], errors, warnings } = await UserActions.LoadMolData(
                    molSource, moleculeLoadOptions, selectedCaseData,
                    { skipHeritage: true },
                );

                // Collect errors and warnings
                if (errors && errors.length > 0) allErrors.push(...errors);
                if (warnings && warnings.length > 0) allWarnings.push(...warnings);

                // Add properties and heritage
                if (compound) {
                    // Row keys the grid or the import itself uses (`SMILES` is the molecule loaded
                    // above), so no job declares them. What a job adds to its rows declares
                    // `excludeFromImport`.
                    const defaultImportExcludes = ['jobtype', 'jobid', 'id', 'SMILES', 'mol'];
                    const dataInfo = jobDefinition.getDataInfo();
                    // A row value that is absent is not a property. Writing one creates a
                    // property that shows as a permanently empty column AND exports as the text
                    // "null": `scopedPropertiesForExport` skips only `undefined`, and `null`
                    // falls through to its `JSON.stringify` branch. DrugFlow's `targetName` and
                    // `pocketLigand` are null on jobs submitted before `requestmeta` recorded
                    // them. `!= null` deliberately, so a legitimate `''` or `0` still counts.
                    const props = Object.keys(importCmpdRow).filter((k) => (
                        !defaultImportExcludes.includes(k)
                        && !dataInfo[k]?.excludeFromImport
                        && importCmpdRow[k] != null
                    ));
                    const propertyScope = jobDefinition.propertyScope;
                    for (const property of props) {
                        compound.setProperty(propertyScope, property, importCmpdRow[property]);
                    }
                    if (addHeritage) addHeritage(compound);
                }
            }

            setCheckedRowItems([]);
            apiRef.current.setRowSelectionModel({ type: 'include', ids: new Set() });
        } finally {
            setLoading(false);
        }

        const combinedErrors = [
            ...allErrors.map((err) => `Error: ${err}`),
            ...allWarnings.map((warn) => `Warning: ${warn}`),
        ];
        setLoadErrors(combinedErrors);
        if (combinedErrors.length === 0 && !continueAdding) {
            closeImportPane();
        }
    }

    function removeMolecules(rows) {
        const removedJobs = {};
        for (const row of rows) {
            const jobId = row.jobid;
            const jobDefinition = lookupJobDefinition(row.jobtype);
            const idField = jobDefinition.idField || 'id';
            const removedCompound = compoundJobs.flatMap((job) => job.structures)
                .find((compound) => compound[idField] === row.id);
            if (!removedCompound) {
                continue;
            }
            if (!removedJobs[jobId]) {
                removedJobs[jobId] = [];
            }
            removedJobs[jobId].push(removedCompound);
        }
        for (const [key, value] of Object.entries(removedJobs)) {
            const currentJobState = localJobState[key];
            const updatedJobState = currentJobState.structures.filter(
                (comp) => !value.includes(comp)
            );

            updateLocalJobState(key, { structures: updatedJobState });
        }
    }

    const { columns, rows } = buildCompoundGrid(
        compoundJobs, importMolecules, removeMolecules, columnWidths
    );

    /**
     * Downloads every row with 3D structures as one SDF file — the grid's full row set,
     * regardless of the current row filter or column visibility (see review discussion on
     * BP2-2718): an export leaving the app should never silently drop rows because of a filter
     * the user set up for an unrelated reason and forgot about.
     */
    function exportSdfFiles() {
        const sdfText = makeSdfFromRows(rows);
        const programName = displayedJobDefinitions.length === 1
            ? displayedJobDefinitions[0].getJobTypeName()
            : 'Compound';
        downloadData(`${programName.replace(/\W+/g, '_')}_Results.sdf`, sdfText, 'chemical/x-mdl-sdfile');
    }

    const placementUIOptions = placementOptions
        .map((option) => {
            const {
                label, disabled, value, disabledMsg='', title='',
            } = option;
            const titleText = disabled ? disabledMsg : title;

            return (
                <MenuItem
                    key={label}
                    value={value}
                    disabled={disabled}
                    title={titleText}
                >
                    {label}
                </MenuItem>
            );
        });

    function handleColumnWidthChange(params) {
        const newWidths = { ...columnWidths, [params.colDef.field]: params.width };
        setColumnWidths(newWidths);
    }

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden',
        }}
        >
            <DialogContentSection
                title={resultsTitle}
                style={{
                    display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden',
                }}
                contentStyle={{
                    display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
                }}
                disablePaddingBottom
            >
                {describedJobDefinitions.map((jobDefinition) => (
                    <Typography
                        key={jobDefinition.getJobTypeName()}
                        variant="body2"
                        sx={{
                            flexShrink: 0,
                            marginBottom: '1rem',
                            color: 'text.secondary',
                            // Descriptions may contain line breaks.
                            whiteSpace: 'pre-line',
                        }}
                    >
                        {jobDefinition.getJobDescription()}
                        {jobDefinition.getJobCitation() && (
                            <>
                                <br />
                                <ExternalLink
                                    link={jobDefinition.getJobCitation()}
                                    style={{ color: 'var(--marketing-blue)' }}
                                >
                                    {`More about ${jobDefinition.getJobTypeName()}`}
                                </ExternalLink>
                            </>
                        )}
                    </Typography>
                ))}
                <DataGrid
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    disableColumnMenu={false}
                    disableRowSelectionOnClick
                    initialState={{
                        density: 'compact',
                        pagination: {
                            paginationModel: { pageSize: 10, page: 0 },
                        },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    checkboxSelection
                    localeText={{
                        footerRowSelected: (count) => `${count} Compound${count !== 1 ? 's' : ''} selected`,
                    }}
                    onRowSelectionModelChange={(model) => {
                        // MUI X DataGrid v9's row selection model is { type, ids: Set } rather
                        // than a plain id array. "select all" switches to 'exclude' mode with an
                        // empty `ids` (nothing excluded, i.e. everything selected), so membership
                        // must be checked against the resolved selection, not `model.ids` itself.
                        const checkedRows = rows.filter((row) => (
                            model.type === 'exclude' ? !model.ids.has(row.id) : model.ids.has(row.id)
                        ));
                        setCheckedRowItems(checkedRows);
                    }}
                    onColumnVisibilityModelChange={(columnVisibilityModel) => {
                        setJobsCompoundsTableState({ columnVisibilityModel });
                    }}
                    // Merge the defaults UNDER the persisted model: the persisted model is shared
                    // by every job type and lists only the columns of whichever was displayed
                    // when it was saved. A column absent from it (another job's, or a newer one)
                    // falls to its default instead of DataGrid's "visible". The user's explicit
                    // show/hide toggles still win.
                    columnVisibilityModel={{
                        ...defaultColumnVisibility,
                        ...(tableState?.columnVisibilityModel || {}),
                        // Not hideable, but it could have been hidden before it was unhideable.
                        Actions: true,
                    }}
                    slots={{
                        toolbar: (toolBarProps) => (
                            <GridToolBar
                                {...toolBarProps}
                                exportFileName="BMapsJobsCompoundsExport"
                                toolBarId="JobsCompoundsTable"
                                // "3D" because that is all this can export today, not because a
                                // 2D SDF is a different thing: any structure, 2D or 3D, converts
                                // to SDF perfectly well. The limit is the source — `row.sdf` is
                                // only ever populated by the job types that return 3D poses
                                // (DrugFlow, Gnina docking), so REINVENT's SMILES-only results
                                // leave this disabled. Naming it "3D SDF" keeps the greyed-out
                                // state honest until a 2D path exists; that path also needs a way
                                // to choose which records go out, since REINVENT can return far
                                // more than a user wants in one file.
                                extraExportOptions={[{
                                    title: 'Download 3D SDF',
                                    disabled: !rows.some((row) => row.sdf),
                                    handleExport: exportSdfFiles,
                                }]}
                            />
                        ),
                        columnsPanel: CustomColumnsPanel,
                        columnHeaderSortIcon: (props) => {
                            const found = columns.find((c) => c.field === props.field);
                            const title = found ? `Sort by ${found.headerName}` : 'Sort';
                            return (
                                <BMapsTooltip title={title} noMaxWidth>
                                    <GridColumnHeaderSortIcon {...props} />
                                </BMapsTooltip>
                            );
                        },
                    }}
                    showToolbar
                    slotProps={{
                        basePopper: { placement: 'bottom-start' },
                    }}
                    onColumnWidthChange={handleColumnWidthChange}
                />
            </DialogContentSection>
            <DialogContentSection
                title="Load Options"
                style={{ display: 'flex', flexDirection: 'column' }}
                contentStyle={{ display: 'flex', flexDirection: 'column' }}
                defaultExpand
                collapsible
                disablePaddingBottom
            >
                <InputBox sx={{
                    display: 'flex', flexDirection: 'row', gap: '1rem', width: '100% !important',
                }}
                >
                    <ProteinSelect style={{ flexGrow: 1 }} />
                    <TextField
                        fullWidth
                        name="molName"
                        label="Compound Prefix (optional)"
                        variant="outlined"
                        value={cmpdName}
                        onChange={(e) => {
                            const error = validateCompoundName(e.target.value);
                            if (error) {
                                setLoadErrors([error]);
                                return;
                            }
                            if (loadErrors.length) setLoadErrors([]);
                            setCmpdName(e.target.value);
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <Select
                        name="placement"
                        label="Compound Placement"
                        slotProps={{ select: { native: false } }}
                        value={placement}
                        onChange={(e) => {
                            setPlacement(e.target.value);
                        }}
                        sx={{ flexGrow: 1 }}
                    >
                        {placementUIOptions}
                    </Select>
                </InputBox>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={continueAdding}
                            onChange={(e) => setContinueAdding(e.target.checked)}
                        />
                    )}
                    label="Continue adding compounds"
                />
            </DialogContentSection>
            <Box sx={{
                display: 'flex', flexDirection: 'row', gap: '1rem', width: '100%', paddingTop: '0.5rem',
            }}
            >
                <BMapsTooltip
                    title={checkedRowItems.length <= 0 ? 'Select a Compound Result to Load' : 'Load Selected Compound Results'}
                    style={{ flexGrow: 1 }}
                >
                    {wrapForDisabledTooltip(
                        <DialogPrimaryButton
                            type="button"
                            variant="contained"
                            sx={{ width: '100%' }}
                            onClick={async () => {
                                await importMolecules(checkedRowItems);
                            }}
                            disabled={checkedRowItems.length <= 0 || loading}
                        >
                            Load Compounds
                        </DialogPrimaryButton>
                    )}
                </BMapsTooltip>
                <BMapsTooltip
                    title={checkedRowItems.length <= 0 ? 'Select a Compound to Remove' : 'Remove Selected Compound Results'}
                    style={{ flexGrow: 1 }}
                >
                    {wrapForDisabledTooltip(
                        <DialogCancelButton
                            type="button"
                            sx={{ width: '100%' }}
                            onClick={() => {
                                removeMolecules(checkedRowItems);
                            }}
                            disabled={checkedRowItems.length <= 0 || loading}
                        >
                            Remove Compounds
                        </DialogCancelButton>
                    )}
                </BMapsTooltip>
            </Box>
            {loadErrors.length > 0 && <ImportErrors errors={loadErrors} />}
        </Box>
    );
}

/**
 * An SDF data field name for a label: "Pocket Ligand" -> "Pocket_Ligand",
 * "Affinity (AutoDock Vina)" -> "Affinity_AutoDock_Vina". Not really SDF-format knowledge (it's
 * a generic label-to-identifier slug), but it has exactly one caller, here, so it lives with it
 * rather than in the shared molecule-format utilities.
 * @param {string} label
 * @returns {string}
 */
function sdfFieldName(label) {
    return label.trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * The SDF data fields for one results-table row: the values of the columns its job DECLARES in
 * `getDataInfo()`, named by column label. Structure columns, columns excluded from the table,
 * and empty values are left out. Declared columns only, on purpose: the grid also shows any
 * undeclared key a row happens to carry, but those would be raw, unlabelled, and mixed in with
 * the grid's own plumbing keys (`jobtype`, `jobid`, `id`), so a job type whose rows have an
 * `sdf` must declare every key it wants exported (DrugFlow and Gnina docking do). Deliberately
 * NOT restricted to currently-visible columns or the current row filter (see review discussion
 * on BP2-2718) — an export leaving the app exports everything, so it can't silently vary with
 * transient table state the user may have set up for an unrelated reason. Lives here rather than on
 * `JobDefinition` — most job types have no per-row `sdf` at all (Reinvent, Folding, MapLight,
 * ...), so "what SDF fields does my row export as" isn't something every job should have to
 * answer; only this tab needs it, for the job types (DrugFlow, Gnina docking) that do.
 * @param {object} row A results-table row, keyed by column.
 * @returns {Object<string, string|number>}
 */
function sdfPropertiesForRow(row) {
    const dataInfo = lookupJobDefinition(row.jobtype)?.getDataInfo() || {};
    const properties = {};
    for (const [key, info] of Object.entries(dataInfo)) {
        if (info.structureValue || info.excludeFromTable) continue;
        // `== null` alone lets `''` through (`makeOneSDF` then emits an empty data field) —
        // deliberately not a bare falsy check, since a legitimate `0` must still be kept.
        if (row[key] == null || row[key] === '') continue;
        properties[sdfFieldName(info.label || key)] = row[key];
    }
    return properties;
}

const MOLBLOCK_END = 'M  END';

/**
 * The molblock of one row's SDF record, discarding whatever data fields it already carries.
 * Every molfile, V2000 or V3000, ends with `M  END`; a row whose `sdf` has none isn't a record
 * (2D-only results such as REINVENT's have no `sdf` at all), and this returns `null`.
 * @param {string} [sdf]
 * @returns {string|null}
 */
function sdfRecordMolblock(sdf) {
    const end = sdf?.indexOf(MOLBLOCK_END);
    return end != null && end !== -1 ? sdf.slice(0, end + MOLBLOCK_END.length) : null;
}

/**
 * One SDF made from results-table rows. Rows without an `sdf` record (2D-only results such as
 * REINVENT's) are skipped.
 * @param {object[]} rows
 * @returns {string}
 */
function makeSdfFromRows(rows) {
    return rows
        .map((row) => ({ row, molblock: sdfRecordMolblock(row?.sdf) }))
        .filter(({ molblock }) => molblock)
        .map(({ row, molblock }) => makeOneSDF(molblock, sdfPropertiesForRow(row)))
        .join('');
}

function getDefaultColumnVisibilityModel(compoundJobs) {
    const columnVisibilityModelEntries = [];
    for (const { job: activeJob, structures } of compoundJobs) {
        const jobDefinition = lookupJobDefinition(activeJob?.jobtype);
        if (jobDefinition) {
            // Collect all property metadata keys, either explicitly defined or from structures
            let keys = new Set(Object.keys(jobDefinition.dataInfo || {}));
            for (const structure of structures) {
                for (const key of Object.keys(structure)) {
                    keys.add(key);
                }
            }
            keys = [...keys];
            // All metadata columns default to not visible unless otherwise stated
            columnVisibilityModelEntries.push(...keys
                .filter((key) => !jobDefinition.dataInfo[key]?.defaultVisible)
                .map((key) => [key, false])); // Preparing entry list for fromEntries below
        }
    }
    return (
        Object.fromEntries(columnVisibilityModelEntries)
    );
}

function buildCompoundGrid(compoundJobs, loadAction, removeAction, columnWidths) {
    const columns = [];
    let rows = [];
    function addOneColumn(key, propInfo={}) {
        if (propInfo.excludeFromTable) return;
        if (columns.find((col) => col.field === key)) return;
        const tooltip = dataInfoTooltip(propInfo);
        const headerText = propInfo.label || key;
        // header tooltip falls back to header text, to preserve availability of truncated headers.
        const headerTooltip = tooltip || headerText;
        const columnInfo = {
            field: key,
            headerName: propInfo.label || key,
            title: propInfo.label || key,
            type: propInfo.type || 'number',
            minWidth: 80,
            width: columnWidths[key],
            flex: columnWidths[key] ? undefined : 1,
            description: tooltip,
            renderCell: ({ value: propValue }) => (
                <BMapsTooltip title={propValue} placement="bottom-start">
                    <span>{getFormattedValue(propValue, propInfo.precision)}</span>
                </BMapsTooltip>
            ),
            renderHeader: () => (
                <BMapsTooltip title={headerTooltip} noMaxWidth>
                    <span>{headerText}</span>
                </BMapsTooltip>
            ),
        };
        if (propInfo.structureValue) {
            columnInfo.renderCell = ({ value, row }) => (
                <HoverableCompoundStructureCell smiles={value} uniqueId={row.id} compoundName={row['Compound Name']} />
            );
            columnInfo.sortable = false;
            columnInfo.filterable = false;
            columnInfo.type = 'string';
        }
        applyColumnDirectionality(columnInfo, propInfo.directionality);
        columns.push(columnInfo);
    }

    for (const { job: activeJob, structures } of compoundJobs) {
        const jobDefinition = lookupJobDefinition(activeJob?.jobtype);
        const knownColumns = jobDefinition?.dataInfo || {};
        const idField = jobDefinition?.idField || 'id';
        // Add explicitly defined columns
        for (const [key, propInfo] of Object.entries(knownColumns)) {
            addOneColumn(key, propInfo);
        }
        for (const structure of structures) {
            const tableRow = {
                ...structure,
                id: structure[idField],
                jobtype: activeJob.jobtype,
                jobid: activeJob.jobid,
            };
            rows.push(tableRow);
            // Add any extra columns that are not explicitly defined
            for (const key of Object.keys(structure)) {
                addOneColumn(key, knownColumns[key]);
            }
        }
    }
    columns.push({
        field: 'Actions',
        headerName: ' ',
        title: 'Compound Actions',
        flex: 1,
        minWidth: 75,
        sortable: false,
        filterable: false,
        hideable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => {
            const noun = 'Compound';
            return (
                <Box>
                    <BMapsTooltip title={`Remove ${noun}`}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                removeAction([row]);
                            }}
                        >
                            <ClearIcon fontSize="inherit" />
                        </IconButton>
                    </BMapsTooltip>
                </Box>
            );
        },
    });
    rows = _.uniqBy(rows, (r) => r.id)
        .filter((r) => r.id);
    if (!rows || rows.length === 0) {
        return { rows: [], columns: [] };
    }
    return { rows, columns };
}

/**
 * The tooltip for a results column, built from its metadata. A column with nothing to say
 * beyond its own name gets none, since a tooltip that only repeats the header is noise.
 * @param {import('BMapsSrc/jobman/JobDefinition').DataInfoEntry} dataInfo
 * @param {string} [heading]
 * @returns {string|JSX.Element|null}
 */
export function dataInfoTooltip(dataInfo, heading) {
    if (!dataInfo) return null;
    if (!hasPropertyTooltipDetail(dataInfo, heading)) return null;
    let tooltip = dataInfo.label;
    const description = dataInfo.description;
    if (description) tooltip += `: ${description}`;
    if (Object.keys(dataInfo).length > 1) {
        const metaDataTooltip = getPropertyTooltip(heading, dataInfo);
        if (metaDataTooltip) tooltip = metaDataTooltip;
    }
    return tooltip;
}
